import CustomTooltip from '../../../shared/CustomTooltip';
import React, { useEffect, useMemo } from 'react';
import {
    Avatar,
    Box,
    Button,
    ButtonGroup,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputLabel,
    ListItemAvatar,
    ListItemText,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    capitalize,
    tableCellClasses,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    DeleteOutline,
    NavigateBefore,
    NavigateNext,
    Close,
} from '@mui/icons-material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { ModalProps } from '../../../types/modal';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import FormTextField from '../../../shared/FormTextField';
import FormMultiAutocomplete from '../../../shared/FormMultiAutocomplete';
import FormCheckbox from '../../../shared/FormCheckbox';
import UserRole from '../../../../shared/AuthRoles';
import {
    useCreateRecurringJobRequestMutation,
    useDeleteRecurringJobMutation,
    useGetIndividualOwnerAssetsQuery,
    useGetJobsQuery,
    useGetOwnerContractorsQuery,
    useUpdateRecurringJobRequestMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import { ProviderType } from '../../../../app/Slices/SevenDayApiTypes';
import {
    camelCaseToTitleCase,
    handleHttpError,
} from '../../../../shared/utils';
import toast from '../../../shared/Toast';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import FormMultiSelectWithALL from '../../../shared/FormMultiSelectWithALL';
import { TimeArray } from '../../Experiences/Owner/Property/TimeArray';
import ContractorJobPolicies from './ContractorJobPolicies';
import { Contractor } from '../../../types/contractor';

const STATUS_COLOR: Record<
    string,
    'blue' | 'yellow' | 'red' | 'green' | 'gray'
> = {
    pending: 'blue',
    completed: 'yellow',
    cancelled: 'red',
    accepted: 'green',
    declined: 'gray',
    pendingCompletion: 'yellow',
};

const RecurrenceSchema = z
    .object({
        contractor: z.object({
            id: z.number().min(0, { message: 'Required' }),
        }),
        isEditing: z.boolean().default(false),
        modules: z.preprocess(
            (val) => {
                if (
                    Array.isArray(val) &&
                    val.every((v) => typeof v === 'number')
                ) {
                    return val.map((id) => ({ id }));
                }
                return val;
            },
            z.array(
                z.object({ id: z.number().min(0, { message: 'Required' }) }),
            ),
        ),
        services: z
            .array(z.string())
            .nonempty({ message: 'At least 1 service required' }),
        jobDay: z.enum(['checkin', 'checkout'], { required_error: 'Required' }),
        startTime: z.string().optional().nullable(),
        endTime: z.string().optional().nullable(),
        notes: z.string().optional().default(''),
        enabled: z.boolean().default(true),
        policy: z
            .object({
                checked: z.boolean(),
            })
            .optional(),
    })
    .superRefine((values, ctx) => {
        if (values.isEditing) return;
        if (!values.policy?.checked) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['policy', 'checked'],
                message: 'Please accept the cancellation policy to continue.',
            });
        }
    });

export type RecurrenceForm = z.infer<typeof RecurrenceSchema>;

interface JobRecurringPreviewProps extends ModalProps {
    id?: number | null;
    jobs?: any[];
    isEditing: boolean;
    index?: number | null;
    onNavigate?: (dir: 'prev' | 'next') => void;
}

const TOOLTIP_SIZE_SX = { color: 'text.primary', fontSize: 16 } as const;

const SCHEDULE_OPTIONS = [
    {
        value: 'checkin',
        label: 'Check-in',
        subtitle: 'Job occurs on the check-in date',
    },
    {
        value: 'checkout',
        label: 'Check-out',
        subtitle: 'Job occurs on the check-out date',
    },
] as const;

const FieldLabel = ({
    children,
    htmlFor,
}: {
    children: React.ReactNode;
    htmlFor?: string;
}) => (
    <InputLabel
        htmlFor={htmlFor}
        sx={{
            fontSize: '12px',
            lineHeight: 1.2,
            fontWeight: 700,
        }}
    >
        {children}
    </InputLabel>
);

const FormField = ({
    label,
    children,
    fieldId,
}: {
    label: React.ReactNode;
    children: React.ReactNode;
    fieldId?: string;
}) => (
    <Box>
        <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>
        {children}
    </Box>
);

const ContractorRecurringModal: React.FC<JobRecurringPreviewProps> = ({
    id,
    jobs,
    isEditing,
    open,
    onClose,
    index,
    onNavigate,
}) => {
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width:700px)');
    const navigate = useNavigate();
    const muiTheme = useTheme();

    const [createRecurringJob] = useCreateRecurringJobRequestMutation();
    const [updateRecurringJob] = useUpdateRecurringJobRequestMutation();
    const [deleteRecurringJob] = useDeleteRecurringJobMutation();

    const currentJob = index != null ? jobs?.[index ?? 0] : null;

    const total = jobs?.length ?? 0;
    const i = index ?? 0;
    const canPrev = total > 0 && i > 0;
    const canNext = total > 0 && i < total - 1;

    const { data: recurringJobs } = useGetJobsQuery(
        { recurringId: currentJob?.id },
        { skip: !isEditing || !currentJob?.id },
    );

    const { data: contractors } = useGetOwnerContractorsQuery(
        loggedInUser.company,
        {
            skip: !loggedInUser?.company,
        },
    );

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft' && canPrev) onNavigate?.('prev');
            if (e.key === 'ArrowRight' && canNext) onNavigate?.('next');
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, canPrev, canNext, onNavigate]);

    const { data: ownerModules, isLoading: isModulesLoading } =
        useGetIndividualOwnerAssetsQuery(
            {
                url: `owners/${loggedInUser?.company}/modules`,
                sort: 'name',
                status: 'published',
            },
            { skip: !loggedInUser?.company },
        );

    const methods = useForm<RecurrenceForm>({
        resolver: zodResolver(RecurrenceSchema),
        defaultValues: isEditing
            ? {
                  contractor: { id: currentJob?.contractor?.id ?? -1 },
                  isEditing: true,
                  modules:
                      currentJob?.modules?.map((m: any) => m?.id) ??
                      ([] as any),
                  services: currentJob?.services ?? [],
                  jobDay:
                      (currentJob?.jobDay as 'checkin' | 'checkout') ??
                      undefined,
                  notes: currentJob?.notes ?? '',
                  enabled: currentJob?.enabled ?? true,
                  startTime: currentJob?.startTime ?? undefined,
                  endTime: currentJob?.endTime ?? undefined,
                  policy: { checked: false },
              }
            : {
                  contractor: { id: -1 },
                  isEditing: false,
                  modules: [],
                  services: [],
                  jobDay: 'checkin',
                  notes: '',
                  enabled: true,
                  startTime: undefined,
                  endTime: undefined,
                  policy: { checked: false },
              },
        mode: 'onChange',
    });

    const {
        control,
        reset,
        handleSubmit,
        setValue,
        formState: { isDirty, isSubmitting },
    } = methods;

    const modulesWatch = useWatch({ name: 'modules', control });
    const contractorWatch = useWatch({ name: 'contractor', control });
    const contractorId = contractorWatch?.id;

    useEffect(() => {
        setValue('isEditing', !!isEditing, { shouldValidate: true });
    }, [isEditing, setValue]);

    useEffect(() => {
        if (!isEditing) return;
        const j = index != null ? jobs?.[index ?? 0] : null;
        reset({
            contractor: { id: j?.contractor?.id ?? -1 },
            modules: j?.modules?.map((m: any) => m?.id) ?? ([] as any),
            services: j?.services ?? [],
            isEditing,
            jobDay: (j?.jobDay as 'checkin' | 'checkout') ?? 'checkin',
            notes: j?.notes ?? '',
            enabled: j?.enabled ?? true,
            startTime: j?.startTime ?? undefined,
            endTime: j?.endTime ?? undefined,
            policy: { checked: false },
        });
    }, [isEditing, index, jobs, reset]);

    const selectedContractor = contractors?.find(
        (c: any) => c?.id === contractorId,
    );
    const availableServices = selectedContractor?.services?.items;

    const moduleOptions = useMemo(() => {
        return (
            ownerModules
                ?.filter((m: any) => m.isAvailable)
                ?.map((m: any) => ({
                    value: m.id,
                    label: camelCaseToTitleCase(m.name),
                    ...m,
                })) ?? []
        );
    }, [ownerModules]);

    const contractorOptions = useMemo(() => {
        return (
            contractors?.map((c: any) => ({
                value: c.id,
                label: c.name,
                ...c,
            })) ?? []
        );
    }, [contractors]);

    const serviceOptions = useMemo(() => {
        return (
            availableServices?.map((s: string) => ({
                value: s,
                label: camelCaseToTitleCase(s),
            })) ?? []
        );
    }, [availableServices]);

    const selectedIds = useMemo(
        () =>
            Array.isArray(modulesWatch)
                ? modulesWatch.map((v: any) =>
                      typeof v === 'number' ? v : v?.id,
                  )
                : [],
        [modulesWatch],
    );

    const selectedModuleOptions = useMemo(
        () =>
            moduleOptions.filter((opt: any) => selectedIds.includes(opt.value)),
        [moduleOptions, selectedIds],
    );

    const idToLabel = useMemo(() => {
        const m = new Map<number, string>();
        moduleOptions.forEach((o: any) =>
            m.set(o.value, o.label ?? o.name ?? ''),
        );
        return m;
    }, [moduleOptions]);

    const getLabel = (o: any): string => {
        if (!o) return '';
        if (o.label) return o.label;
        if (o.name) return o.name;
        const idVal = typeof o === 'number' ? o : (o.id ?? o.value);
        return idToLabel.get(idVal) ?? '';
    };

    const getStatusColor = (
        status: string,
    ): 'blue' | 'green' | 'yellow' | 'red' | 'gray' => {
        return STATUS_COLOR[status] ?? 'gray';
    };

    const closeAndReset = () => {
        onClose?.();
        reset();
    };

    const handleCreate = async (data: RecurrenceForm) => {
        try {
            const newJob = await createRecurringJob(data).unwrap();
            toast.success('Recurring job request sent successfully');
            closeAndReset();
            navigate(
                `/recurringJobs?enabled=${newJob.enabled ? 'true' : 'false'}`,
            );
        } catch {
            toast.error('Error sending recurring job request');
        }
    };

    const handleUpdate = async (data: RecurrenceForm) => {
        try {
            const payload: [number | undefined, RecurrenceForm] = [
                currentJob?.id,
                data,
            ];
            const res: any = await updateRecurringJob(payload);
            if (res?.error) toast.error(handleHttpError(res.error));
            else {
                toast.success('Recurring job updated');
                closeAndReset();
            }
        } catch {
            toast.error('Failed to update recurring job');
        }
    };

    const handleDelete = async (rid: number) => {
        try {
            const res: any = await deleteRecurringJob(rid);
            if (res?.error) toast.error(handleHttpError(res.error));
            else {
                toast.success('Recurring job deleted');
                closeAndReset();
                const wasEnabled = currentJob?.enabled ?? true;
                navigate(
                    `/recurringJobs?enabled=${wasEnabled ? 'true' : 'false'}`,
                );
            }
        } catch {
            toast.error('Failed to delete recurring job');
        }
    };

    const onSubmit = (form: RecurrenceForm) =>
        isEditing ? handleUpdate(form) : handleCreate(form);

    const handleViewJob = (
        jobId: number,
        status: 'pending' | 'accepted' | 'completed' | 'declined' | 'cancelled',
    ) =>
        navigate(
            `/jobList?selectedJob=${jobId}&status=${status}&provider-type=${ProviderType.Contractor}`,
        );

    const sortedJobs = React.useMemo(() => {
        const list = (recurringJobs ?? []).slice();
        list.sort((a: any, b: any) => {
            const at = new Date(a.jobDate).getTime();
            const bt = new Date(b.jobDate).getTime();
            return at - bt;
        });
        return list;
    }, [recurringJobs]);

    return (
        <Dialog
            open={!!open}
            onClose={closeAndReset}
            maxWidth='md'
            fullWidth
            PaperProps={{
                sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'auto',
                    width: '100%',
                    maxWidth: { xs: '100%', md: '1000px' },
                },
            }}
        >
            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    }}
                >
                    <DialogTitle
                        sx={{
                            alignItems: 'flex-start',
                            gap: 1,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flex: 1,
                                minWidth: 0,
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: isMobile ? 'flex-start' : 'center',
                                justifyContent: isMobile
                                    ? 'flex-start'
                                    : 'space-between',
                                gap: 1,
                            }}
                        >
                            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    <EventRepeatIcon
                                        sx={{
                                            fontSize: '20px',
                                            color: muiTheme.palette.textAccent
                                                .main,
                                        }}
                                    />

                                    {isEditing ? (
                                        <Box
                                            sx={{
                                                ml: 0.5,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Recurring Job #{currentJob?.id}
                                        </Box>
                                    ) : (
                                        <>New Recurring Job</>
                                    )}
                                </Box>

                                <Typography sx={{ fontSize: 10 }}>
                                    Note: A recurring job must be 'Active' in
                                    order for the system to generate jobs for
                                    future bookings.
                                </Typography>
                            </Stack>

                            {isEditing && !isMobile && (
                                <ButtonGroup
                                    size='small'
                                    variant='outlined'
                                    sx={{ height: 30 }}
                                >
                                    <Button
                                        startIcon={<NavigateBefore />}
                                        onClick={() => onNavigate?.('prev')}
                                        disabled={!canPrev}
                                    >
                                        Prev
                                    </Button>
                                    <Button
                                        endIcon={<NavigateNext />}
                                        onClick={() => onNavigate?.('next')}
                                        disabled={!canNext}
                                    >
                                        Next
                                    </Button>
                                </ButtonGroup>
                            )}
                        </Box>

                        <IconButton
                            onClick={closeAndReset}
                            size='small'
                            aria-label='close'
                            sx={{ color: 'text.primary' }}
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            width: '100%',
                            px: { xs: 1, sm: 3 },
                        }}
                    >
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <FormField
                                    label='Contractor'
                                    fieldId='recurring-job-contractor__input'
                                >
                                    <FormAutocomplete
                                        id='recurring-job-contractor__input'
                                        name='contractor.id'
                                        control={control}
                                        options={contractorOptions}
                                        placeholder={
                                            contractorOptions?.length
                                                ? ''
                                                : 'Select contractor'
                                        }
                                        renderOption={(
                                            props: any,
                                            option: any,
                                        ) => (
                                            <li key={option.value} {...props}>
                                                <ListItemText
                                                    primary={option.label}
                                                    secondary={
                                                        option.services &&
                                                        capitalize(
                                                            option.services
                                                                .type,
                                                        )
                                                    }
                                                />
                                            </li>
                                        )}
                                    />
                                </FormField>
                            </Grid>

                            <Grid item xs={12}>
                                <FormField
                                    fieldId='recurring-job-experiences__input'
                                    label={
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                            }}
                                        >
                                            Experiences
                                            <CustomTooltip
                                                title={
                                                    <Typography>
                                                        Select the experiences
                                                        to auto-generate a job
                                                        each time a reservation
                                                        is approved.
                                                    </Typography>
                                                }
                                                placement='right'
                                            >
                                                <HelpOutlineIcon
                                                    sx={TOOLTIP_SIZE_SX}
                                                />
                                            </CustomTooltip>
                                        </Box>
                                    }
                                >
                                    <FormMultiSelectWithALL
                                        id='recurring-job-experiences__input'
                                        name='modules'
                                        control={control}
                                        options={moduleOptions}
                                        placeholder={
                                            isModulesLoading
                                                ? 'Loading…'
                                                : moduleOptions.length
                                                  ? ''
                                                  : 'Select experience'
                                        }
                                        value={selectedModuleOptions}
                                        getOptionLabel={getLabel}
                                        renderOption={(
                                            props: any,
                                            option: any,
                                        ) => (
                                            <li {...props}>
                                                <ListItemAvatar>
                                                    <Avatar
                                                        variant='rounded'
                                                        src={
                                                            option.photos?.[0]
                                                                ?.src || ''
                                                        }
                                                    />
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        option.name ??
                                                        option.label
                                                    }
                                                    secondary={`${option.streetAddress ?? ''}${option.suburb ? `, ${option.suburb}` : ''}`}
                                                />
                                            </li>
                                        )}
                                        renderTags={(
                                            selected: any[],
                                            getTagProps: any,
                                        ) => {
                                            const shown = selected.slice(0, 4);
                                            const extra =
                                                selected.length - shown.length;
                                            return [
                                                ...shown.map((opt, idx2) => (
                                                    <Chip
                                                        {...getTagProps({
                                                            index: idx2,
                                                        })}
                                                        key={opt.value ?? idx2}
                                                        size='small'
                                                        label={
                                                            opt.label ??
                                                            opt.name
                                                        }
                                                    />
                                                )),
                                                extra > 0 && (
                                                    <Chip
                                                        key='extra'
                                                        size='small'
                                                        variant='outlined'
                                                        label={`+${extra}`}
                                                    />
                                                ),
                                            ];
                                        }}
                                        onChange={(_, selected: any[]) => {
                                            setValue(
                                                'modules',
                                                selected.map((opt: any) => ({
                                                    id: opt.value,
                                                })),
                                                { shouldDirty: true },
                                            );
                                        }}
                                    />
                                </FormField>
                            </Grid>

                            <Grid item container spacing={2} xs={12}>
                                <Grid item xs={12} sm={6}>
                                    <FormField
                                        label='Start Time'
                                        fieldId='recurring-job-start-time__input'
                                    >
                                        <FormAutocomplete
                                            id='recurring-job-start-time__input'
                                            name='startTime'
                                            control={control}
                                            options={TimeArray}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormField
                                        label='End Time'
                                        fieldId='recurring-job-end-time__input'
                                    >
                                        <FormAutocomplete
                                            id='recurring-job-end-time__input'
                                            name='endTime'
                                            control={control}
                                            options={TimeArray}
                                        />
                                    </FormField>
                                </Grid>
                            </Grid>

                            <Grid container item spacing={1}>
                                <Grid item xs={12} lg={6}>
                                    <FormField
                                        fieldId='recurring-job-schedule__input'
                                        label={
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.75,
                                                }}
                                            >
                                                Schedule For
                                                <CustomTooltip
                                                    title={
                                                        <Typography>
                                                            Choose whether the
                                                            job should occur on
                                                            the check-in date
                                                            (e.g., catering) or
                                                            the check-out date
                                                            (e.g., cleaning).
                                                        </Typography>
                                                    }
                                                    placement='right'
                                                >
                                                    <HelpOutlineIcon
                                                        sx={TOOLTIP_SIZE_SX}
                                                    />
                                                </CustomTooltip>
                                            </Box>
                                        }
                                    >
                                        <FormAutocomplete
                                            id='recurring-job-schedule__input'
                                            name='jobDay'
                                            control={control}
                                            options={SCHEDULE_OPTIONS.map(
                                                (s) => ({
                                                    value: s.value,
                                                    label: s.label,
                                                }),
                                            )}
                                            renderOption={(
                                                props: any,
                                                option: any,
                                            ) => (
                                                <li
                                                    key={option.value}
                                                    {...props}
                                                >
                                                    <ListItemText
                                                        primary={option.label}
                                                        secondary={
                                                            SCHEDULE_OPTIONS.find(
                                                                (s) =>
                                                                    s.value ===
                                                                    option.value,
                                                            )?.subtitle
                                                        }
                                                    />
                                                </li>
                                            )}
                                        />
                                    </FormField>
                                </Grid>

                                <Grid item xs>
                                    <FormField
                                        fieldId='recurring-job-services__input'
                                        label={
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.75,
                                                }}
                                            >
                                                Services
                                                <CustomTooltip
                                                    title={
                                                        <Typography>
                                                            Select the services
                                                            to perform for each
                                                            generated job.
                                                        </Typography>
                                                    }
                                                    placement='right'
                                                >
                                                    <HelpOutlineIcon
                                                        sx={TOOLTIP_SIZE_SX}
                                                    />
                                                </CustomTooltip>
                                            </Box>
                                        }
                                    >
                                        <FormMultiAutocomplete
                                            id='recurring-job-services__input'
                                            name='services'
                                            control={control}
                                            fullWidth
                                            disabled={!serviceOptions?.length}
                                            placeholder={
                                                serviceOptions?.length
                                                    ? ''
                                                    : 'Select contractor first'
                                            }
                                            options={serviceOptions}
                                        />
                                    </FormField>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <FormField
                                    fieldId='recurring-job-notes__input'
                                    label={
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                            }}
                                        >
                                            Notes
                                            <CustomTooltip
                                                title={
                                                    <Typography>
                                                        Add instructions,
                                                        timing, access details,
                                                        or anything your
                                                        contractor should know.
                                                    </Typography>
                                                }
                                                placement='right'
                                            >
                                                <HelpOutlineIcon
                                                    sx={TOOLTIP_SIZE_SX}
                                                />
                                            </CustomTooltip>
                                        </Box>
                                    }
                                >
                                    <FormTextField
                                        id='recurring-job-notes__input'
                                        name='notes'
                                        control={control}
                                        multiline
                                        rows={4}
                                    />
                                </FormField>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <FormCheckbox
                                    name='enabled'
                                    control={control}
                                    sx={{ p: 0 }}
                                    inputProps={{ 'aria-label': 'Active' }}
                                />
                                <FieldLabel>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                        }}
                                    >
                                        Active
                                        <CustomTooltip
                                            title={
                                                <Typography>
                                                    Uncheck to deactivate.
                                                    Future jobs won’t be
                                                    generated, but existing jobs
                                                    remain.
                                                </Typography>
                                            }
                                            placement='right'
                                        >
                                            <HelpOutlineIcon
                                                sx={TOOLTIP_SIZE_SX}
                                            />
                                        </CustomTooltip>
                                    </Box>
                                </FieldLabel>
                            </Grid>

                            {!isEditing && selectedContractor && (
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <ContractorJobPolicies
                                        contractor={
                                            selectedContractor as Contractor
                                        }
                                        isRecurring={true}
                                    />
                                </Grid>
                            )}

                            {isEditing && (
                                <>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12}>
                                            <Typography
                                                color='text.primary'
                                                sx={{ pt: 2, fontSize: 18 }}
                                            >
                                                Individual Jobs
                                            </Typography>
                                            <Typography
                                                color='text.primary'
                                                sx={{ fontSize: 12, pb: 2 }}
                                            >
                                                The jobs below were
                                                automatically created by this
                                                recurring job.
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    <Box
                                        sx={{
                                            width: '100%',
                                            border: `1px solid ${muiTheme.palette.custom.lightBorderColor}`,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {!recurringJobs?.length ? (
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    pt: '20px',
                                                    pb: '20px',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                No jobs have been created
                                            </Typography>
                                        ) : (
                                            <TableContainer
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: '100%',
                                                    border: `1px solid ${muiTheme.palette.custom.lightBorderColor}`,
                                                    height: isMobile
                                                        ? '200px'
                                                        : '150px',
                                                    overflowY: 'auto',
                                                    position: 'relative',
                                                }}
                                            >
                                                <Table
                                                    stickyHeader
                                                    size='small'
                                                    sx={{
                                                        width: '100%',
                                                        tableLayout: 'fixed',
                                                        [`& .${tableCellClasses.root}`]:
                                                            {
                                                                borderBottom: `1px solid ${muiTheme.palette.custom.lightBorderColor}`,
                                                            },
                                                    }}
                                                >
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell
                                                                sx={{
                                                                    width: 80,
                                                                }}
                                                            >
                                                                Job ID
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    width: 110,
                                                                }}
                                                            >
                                                                Reservation
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    width: 260,
                                                                }}
                                                            >
                                                                Experience
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    width: 180,
                                                                }}
                                                            >
                                                                Contractor
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    width: 120,
                                                                }}
                                                            >
                                                                Date
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    width: 140,
                                                                }}
                                                            >
                                                                Status
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    width: 36,
                                                                }}
                                                            />
                                                        </TableRow>
                                                    </TableHead>

                                                    <TableBody>
                                                        {sortedJobs?.length ? (
                                                            sortedJobs.map(
                                                                (job: any) => (
                                                                    <TableRow
                                                                        key={
                                                                            job.id
                                                                        }
                                                                        hover
                                                                        sx={{
                                                                            cursor: 'pointer',
                                                                        }}
                                                                        onClick={() =>
                                                                            handleViewJob(
                                                                                job.id,
                                                                                job.status,
                                                                            )
                                                                        }
                                                                    >
                                                                        <TableCell>
                                                                            #
                                                                            {
                                                                                job.id
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            #
                                                                            {
                                                                                job
                                                                                    ?.booking
                                                                                    ?.id
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {
                                                                                job
                                                                                    ?.module
                                                                                    ?.name
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {
                                                                                job
                                                                                    ?.contractor
                                                                                    ?.name
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {new Date(
                                                                                job?.jobDate,
                                                                            ).toLocaleDateString(
                                                                                'en-AU',
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <CustomChip
                                                                                cursor='pointer'
                                                                                label={camelCaseToTitleCase(
                                                                                    job.status,
                                                                                )}
                                                                                color={getStatusColor(
                                                                                    job.status,
                                                                                )}
                                                                                variant='outlined'
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell
                                                                            sx={{
                                                                                width: 36,
                                                                                maxWidth: 36,
                                                                                whiteSpace:
                                                                                    'nowrap',
                                                                            }}
                                                                            onClick={(
                                                                                e,
                                                                            ) =>
                                                                                e.stopPropagation()
                                                                            }
                                                                        >
                                                                            <CustomIconButton
                                                                                onClick={() =>
                                                                                    handleViewJob(
                                                                                        job.id,
                                                                                        job.status,
                                                                                    )
                                                                                }
                                                                                sx={{
                                                                                    '&:hover':
                                                                                        {
                                                                                            cursor: 'pointer',
                                                                                            color: muiTheme
                                                                                                .palette
                                                                                                .textAccent
                                                                                                .main,
                                                                                            backgroundColor:
                                                                                                'transparent',
                                                                                            background:
                                                                                                'none',
                                                                                        },
                                                                                }}
                                                                            >
                                                                                <ArrowOutwardIcon
                                                                                    sx={{
                                                                                        fontSize:
                                                                                            '14px',
                                                                                    }}
                                                                                />
                                                                            </CustomIconButton>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ),
                                                            )
                                                        ) : (
                                                            <TableRow>
                                                                <TableCell
                                                                    colSpan={7}
                                                                    sx={{
                                                                        height: '200px',
                                                                        textAlign:
                                                                            'center',
                                                                        verticalAlign:
                                                                            'middle',
                                                                        color: 'text.primary',
                                                                        fontSize: 12,
                                                                    }}
                                                                >
                                                                    No jobs have
                                                                    been created
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </Box>
                                </>
                            )}
                        </Grid>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 1,
                            justifyContent: 'space-between',
                        }}
                    >
                        <Stack
                            direction={isMobile ? 'column' : 'row'}
                            spacing={1}
                        >
                            {isEditing && isMobile && (
                                <ButtonGroup
                                    size='small'
                                    variant='outlined'
                                    sx={{ height: 30 }}
                                >
                                    <Button
                                        startIcon={<NavigateBefore />}
                                        onClick={() => onNavigate?.('prev')}
                                        disabled={!canPrev}
                                    >
                                        Prev
                                    </Button>
                                    <Button
                                        endIcon={<NavigateNext />}
                                        onClick={() => onNavigate?.('next')}
                                        disabled={!canNext}
                                    >
                                        Next
                                    </Button>
                                </ButtonGroup>
                            )}

                            {!isMobile &&
                                typeof index === 'number' &&
                                (jobs?.length ?? 0) > 0 && (
                                    <Typography
                                        component='span'
                                        sx={{ fontWeight: 400, fontSize: 14 }}
                                    >
                                        ({index + 1} of {jobs!.length} Jobs)
                                    </Typography>
                                )}
                        </Stack>

                        <Stack direction='row' spacing={0.5}>
                            {isEditing && (
                                <CustomIconButton
                                    variant='contained'
                                    onClick={() => handleDelete(currentJob?.id)}
                                    sx={{
                                        '&:hover': {
                                            cursor: 'pointer',
                                            color: 'red',
                                            backgroundColor: 'transparent',
                                            background: 'none',
                                        },
                                    }}
                                >
                                    <DeleteOutline sx={{ fontSize: '16px' }} />
                                </CustomIconButton>
                            )}

                            <Button
                                type='button'
                                variant='outlined'
                                color='primary'
                                onClick={closeAndReset}
                            >
                                {isEditing ? 'Close' : 'Cancel'}
                            </Button>

                            <CustomButton
                                variant='contained'
                                color='primary'
                                type='submit'
                                disabled={isSubmitting || !isDirty}
                            >
                                {isEditing ? 'Update' : 'Request'}
                            </CustomButton>
                        </Stack>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default ContractorRecurringModal;
